import {
    updateCompany,
    updateCompanyToken,
    updateCompanySubscriptionDates,
    uploadCompanyLogo,
    deleteCompany
} from "../../requests.js"
import React, { useState, useEffect, useRef } from "react";
import { MoreVerticalIcon, Pencil, ChevronsUpDown } from "lucide-react"
import "../../_css/UserManagement.css"
import { notifyError, notifySuccess } from "../../utils/helper.js";
import DeleteCompanyDataModal from "../Modals/DeleteCompanyDataModal.js";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer.js";
import { useDispatch } from "react-redux";

const CompanyTable = ({ filtereddata, startIndex, onDataUpdate, sortField, sortOrder, onSort }) => {
    const [menuOpenIndex, setMenuOpenIndex] = useState(null);
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [deleteModal, setDeleteModal] = useState({ show: false, companyId: null, companyName: "" });
    const menuRef = useRef(null);
    const dispatch = useDispatch();

    useEffect(() => {
        setSelectedRows(new Set());
    }, [filtereddata]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpenIndex(null);
            }
        };
        if (menuOpenIndex !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpenIndex]);

    const handleSort = (key) => {
        if (onSort) {
            const newOrder = sortField === key && sortOrder === "asc" ? "desc" : "asc";
            onSort(key, newOrder);
        }
    };

    const getSortIcon = (field) => {
        if (sortField === field) {
            return sortOrder === "asc" ? "↑" : "↓";
        }
        return <ChevronsUpDown size={16} />;
    };

    const toggleMenu = (idx) => {
        setMenuOpenIndex(prevIndex => prevIndex === idx ? null : idx);
    };

    const toggleRowSelection = (index) => {
        const newSelected = new Set(selectedRows);
        newSelected.has(index) ? newSelected.delete(index) : newSelected.add(index);
        setSelectedRows(newSelected);
    };

    const toggleAllSelection = () => {
        if (selectedRows.size === filtereddata.length) {
            setSelectedRows(new Set());
        } else {
            setSelectedRows(new Set([...Array(filtereddata.length).keys()]));
        }
    };

    const openDeleteModal = (companyId, companyName) => {
        setDeleteModal({ show: true, companyId, companyName });
        setMenuOpenIndex(null);
    };

    const closeDeleteModal = () => {
        setDeleteModal({ show: false, companyId: null, companyName: "" });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModal.companyId) return;
        dispatch(startLoader());
        try {
            const response = await deleteCompany(deleteModal.companyId);
            if (response.data?.success) {
                notifySuccess("Company Deleted Successfully");
                onDataUpdate?.();
                closeDeleteModal();
            }
        } catch (error) {
            console.error('Error deleting company:', error);
        } finally {
            dispatch(stopLoader());
        }
    };

    const startEditing = (rowIndex, field, value) => {
        setEditingCell({ rowIndex, field });
        setEditValue(value || '');
    };

    const saveEditing = async () => {
        if (!editingCell) return;
        const { rowIndex, field } = editingCell;
        const row = filtereddata[rowIndex];
        const rowId = row.company_id;
        let existingValue = field === "name" ? (row.user?.name ?? "") : (row[field] ?? "");
        const newValue = String(editValue ?? "").trim();
        const oldValue = String(existingValue ?? "").trim();

        if (newValue === oldValue) {
            setEditingCell(null);
            return;
        }

        dispatch(startLoader());
        try {
            let response;
            if (field === "token" || field === "LLM_tokens" || field === "heyGen_tokens") {
                const tokenData = {};
                if (field === "token") tokenData.token = newValue;
                else if (field === "LLM_tokens") tokenData.llm_tokens = newValue;
                else if (field === "heyGen_tokens") tokenData.heygen_tokens = newValue;

                response = await updateCompanyToken({ rowId, tokenData });
            }
            else if (field === "startDate" || field === "endDate") {
                response = await updateCompanySubscriptionDates(rowId, {
                    startDate: field === "startDate" ? newValue : row.startDate,
                    endDate: field === "endDate" ? newValue : row.endDate
                });
            }
            else if (field === "name") {
                response = await updateCompany(rowId, { name: newValue });
            }
            else {
                response = await updateCompany(rowId, { [field]: newValue });
            }

            if (response.data?.success) {
                notifySuccess(`${field} updated successfully`);
                onDataUpdate?.();
            } else {
                const message = response.data?.message || `Failed to update ${field}`;
                notifyError(message);
            }
        } catch (error) {
            console.error("Error updating data:", error);
            notifyError(error.response.data.message || `Error updating ${field}`);
        } finally {
            dispatch(stopLoader());
            setEditingCell(null);
            setEditValue("");
        }
    };


    const handleMenuAction = async (action, rowIndex) => {
        const rowId = filtereddata[rowIndex].company_id;
        const companyName = filtereddata[rowIndex].user?.name || "Company";

        if (action?.toLowerCase() === "delete company") {
            openDeleteModal(rowId, companyName);
        } else if (action?.toLowerCase() === "add profile image") {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = 'image/*';
            fileInput.onchange = async (e) => {
                const file = e.target.files[0];
                if (file) {
                    dispatch(startLoader());
                    try {
                        const response = await uploadCompanyLogo(rowId, file);
                        if (response.data?.success) {
                            notifySuccess("Profile image uploaded successfully");
                            onDataUpdate?.();
                        }
                    } catch (error) {
                        console.error('Error uploading logo:', error);
                    } finally {
                        dispatch(stopLoader());
                    }
                }
            };
            fileInput.click();
        }
        setMenuOpenIndex(null);
    };

    const getDisplayValue = (value, field) => {
        if (value === null || value === undefined) return "N/A";
        if (['token', 'LLM_tokens', 'heyGen_tokens'].includes(field) && typeof value === 'string') {
            return value.length > 8 ? `${value.slice(0, 8)}...` : value;
        }

        if (field === 'plan') {
            return value === 'Expired' ? <span className="badge bg-danger-subtle text-danger">Expired</span> : <span className="badge bg-success-subtle text-success">Active</span>;

        }
        return String(value);
    };

    const getColumns = (row) => [
        { value: row.email, field: 'email', editable: true },
        { value: row.license, field: 'license', editable: true },
        { value: row.usersCount, field: 'usersCount', editable: false },
        { value: row.startDate, field: 'startDate', editable: true },
        { value: row.endDate, field: 'endDate', editable: true },
        { value: row.token, field: 'token', editable: false },
        { value: row.tokens_used, field: 'tokens_used', editable: false },
        { value: row.LLM_tokens, field: 'LLM_tokens', editable: true },
        { value: row.LLM_tokens_used, field: 'LLM_tokens_used', editable: false },
        { value: row.LLM_tokens_remaining, field: 'LLM_tokens_remaining', editable: false },
        { value: row.heyGen_tokens, field: 'heyGen_tokens', editable: true },
        { value: row.heyGen_tokens_used, field: 'heyGen_tokens_used', editable: false },
        { value: row.heyGen_tokens_remaining, field: 'heyGen_tokens_remaining', editable: false },
        { value: row.plan, field: 'plan', editable: false },
        { value: "edit", field: 'edit', editable: false }

    ];

    return (
        <>
            <div className="company-table-container custom-scrollbar">
                <div className="company-table-min-width">
                    <table className="company-table text-center align-middle">
                        <thead className="company-table-header">
                            <tr>
                                <th className="company-table-header-cell company-table-header-cell-first">
                                    <div className="d-flex align-items-center">
                                        <input
                                            type="checkbox"
                                            className="form-check-input me-2"
                                            checked={selectedRows.size === filtereddata.length && filtereddata.length > 0}
                                            onChange={toggleAllSelection}
                                        />
                                        <span>S.L</span>
                                    </div>
                                </th>

                                <th className="company-table-header-cell company-table-header-name" onClick={() => handleSort("name")} style={{ cursor: 'pointer' }}>
                                    Name <span className="sort-icon">{getSortIcon("name")}</span>
                                </th>

                                <th className="company-table-header-cell" onClick={() => handleSort("email")} style={{ cursor: 'pointer' }}>
                                    Email <span className="sort-icon">{getSortIcon("email")}</span>
                                </th>

                                <th className="company-table-header-cell">License</th>
                                <th className="company-table-header-cell">Total Users</th>
                                <th className="company-table-header-cell company-table-header-date" onClick={() => handleSort("startDate")} style={{ cursor: 'pointer' }}>
                                    Start date <span className="sort-icon">{getSortIcon("startDate")}</span>
                                </th>
                                <th className="company-table-header-cell company-table-header-date" onClick={() => handleSort("endDate")} style={{ cursor: 'pointer' }}>
                                    End date <span className="sort-icon">{getSortIcon("endDate")}</span>
                                </th>
                                <th className="company-table-header-cell">Token</th>
                                <th className="company-table-header-cell">Token Used</th>
                                <th className="company-table-header-cell">LLM Tokens</th>
                                <th className="company-table-header-cell">LLM Used</th>
                                <th className="company-table-header-cell">LLM Remaining</th>
                                <th className="company-table-header-cell">HeyGen Tokens</th>
                                <th className="company-table-header-cell">HeyGen Used</th>
                                <th className="company-table-header-cell">HeyGen Remaining</th>
                                <th className="company-table-header-cell">Status</th>
                                <th className="company-table-header-cell company-table-header-edit  company-table-header-cell-last">Edit</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtereddata.map((row, index) => {
                                const isInactive = row.status === 'Inactive';
                                const columns = getColumns(row);
                                const isLastRow = index === filtereddata.length - 1;

                                return (
                                    <tr
                                        key={row.company_id || index}
                                        className={`company-table-row ${isInactive ? "company-table-row-inactive" : ""}`}
                                        style={isInactive ? { opacity: 0.6, backgroundColor: 'rgba(255, 0, 0, 0.1)' } : {}}
                                    >
                                        <td className="company-table-cell company-table-cell-first">
                                            <div className="d-flex align-items-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input me-3"
                                                    checked={selectedRows.has(index)}
                                                    onChange={() => toggleRowSelection(index)}
                                                />
                                                <span className="company-table-serial">
                                                    {String(startIndex + index + 1).padStart(2, '0')}
                                                </span>
                                            </div>
                                        </td>

                                        <td className="company-table-cell">
                                            <div className="d-flex align-items-center">
                                                <span className="company-table-avatar me-3 ">
                                                    {row.user?.profile
                                                        ? <img src={row.user.profile} alt="" width="32" height="32" style={{ objectFit: "cover" }} />
                                                        : "👤"}
                                                </span>

                                                {editingCell?.rowIndex === index && editingCell?.field === 'name' ? (
                                                    <input
                                                        autoFocus
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && saveEditing()}
                                                        onBlur={saveEditing}
                                                        className="company-table-edit-input"
                                                    />
                                                ) : (
                                                    <span
                                                        className="d-flex align-items-center gap-1"
                                                        onClick={() => startEditing(index, 'name', row.user?.name)}
                                                    >
                                                        {getDisplayValue(row.user?.name, 'name')}
                                                        <Pencil size={14} color="#23B4C3" />
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        {columns.map((col, idx) => {
                                            const isDateColumn = col.field === 'startDate' || col.field === 'endDate';
                                            const isEditing = editingCell?.rowIndex === index && editingCell?.field === col.field;
                                            return (
                                                <td key={idx} className={`company-table-cell ${col.field === 'edit' ? "company-table-cell-sticky" : ""}`}>
                                                    {col.editable ? (
                                                        isEditing ? (
                                                            <input
                                                                autoFocus
                                                                type={isDateColumn ? "date" : "text"}
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') saveEditing();
                                                                    if (e.key === 'Escape') {
                                                                        setEditingCell(null);
                                                                        setEditValue("");
                                                                    }
                                                                }}
                                                                onBlur={saveEditing}
                                                                className="company-table-edit-input"
                                                                style={isDateColumn ? { width: '150px' } : {}}
                                                            />
                                                        ) : (
                                                            <span
                                                                className="d-flex align-items-center gap-1"
                                                                onClick={() => startEditing(index, col.field, col.value)}
                                                            >
                                                                {getDisplayValue(col.value, col.field)}
                                                                <Pencil size={14} color="#23B4C3" />
                                                            </span>
                                                        )
                                                    ) : col.field === 'edit' ? (
                                                        // Only show the menu icon if the row is NOT inactive
                                                        !isInactive && (
                                                            <div className="company-table-menu-container" ref={menuOpenIndex === index ? menuRef : null}>
                                                                <MoreVerticalIcon onClick={() => toggleMenu(index)} style={{ cursor: 'pointer' }} />
                                                                {menuOpenIndex === index && (
                                                                    <div
                                                                        className="company-table-dropdown-menu"
                                                                        style={{
                                                                            zIndex: 5,
                                                                            // If it's the last row, make it open UPWARDS so it doesn't get cut off or merged
                                                                            ...(isLastRow ? { bottom: '95%', top: 'auto' } : {})
                                                                        }}
                                                                    >
                                                                        <div
                                                                            onClick={() => handleMenuAction("Add Profile Image", index)}
                                                                            className="company-table-dropdown-item"
                                                                        >
                                                                            Add Profile Image
                                                                        </div>
                                                                        <div
                                                                            onClick={() => handleMenuAction("Delete Company", index)}
                                                                            className="company-table-dropdown-item user-table-dropdown-item-danger"
                                                                        >
                                                                            Delete Company
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )
                                                    ) : getDisplayValue(col.value, col.field)}
                                                </td>
                                            );
                                        })}

                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <DeleteCompanyDataModal
                show={deleteModal.show}
                onHide={closeDeleteModal}
                onSubmit={handleDeleteConfirm}
            />
        </>
    );
};

export default CompanyTable;